/**
 * Platform past papers — CWY's own board papers, set once for every school.
 *
 * A platform operator stages a paper (typed in, or a PDF read by AI), reviews
 * every row — question, options, the key, marks, time, TEAM, video and the
 * sub-topic it is filed under — and publishes it GLOBAL with its exam year. It
 * then reaches every student on that board and grade through Tests & papers →
 * Past papers, and "Beat your best" once they have sat it.
 *
 * The stage is the same `CustomPaper` row a teacher's draft uses, flagged
 * `isGlobal` and held in the first-party workspace, so the editor, the TEAM
 * drafting and the extraction job are the teacher's own rather than a copy.
 *
 * The second half of this file is the help-content file format,
 * `cwy.paperhelp/1`: the per-question support of a published paper (hints,
 * theory line, worked steps, video, common mistakes), downloaded and uploaded as
 * one JSON document. `docs/PAPER_HELP.md` explains it for authors.
 */
import { z } from 'zod';
import { teacherPaperItemBody, zBoard, zGrade } from './api';
import type { Board, CustomPaperItem, CustomPaperStatus, Grade } from './domain';

// ─────────────────────────── staging a paper ───────────────────────────

/** A board paper's year. Generous on both sides; the year is the operator's to state. */
export const zExamYear = z
  .number({ error: 'Give the year of the exam.' })
  .int()
  .min(1990, { error: 'Give the year of the exam.' })
  .max(2100, { error: 'Give the year of the exam.' });

/**
 * What every staged paper starts with. Board and grade are NOT in it: they are
 * read from the subject, so a paper cannot claim a cohort its subject is not in.
 */
export const platformPaperDraftBody = z.object({
  title: z.string().trim().min(1).max(200),
  subjectId: z.string().min(1),
  examYear: zExamYear,
});

/** "Type manually": a blank draft, or one holding rows already written. */
export const platformPaperCreateBody = platformPaperDraftBody.extend({
  items: z.array(teacherPaperItemBody).max(60).optional(),
});

/** Registers a PDF the operator has already PUT to S3 through `POST /platform/papers/presign`. */
export const platformPaperUploadBody = platformPaperDraftBody.extend({
  s3Key: z.string().min(1).max(300),
  fileName: z.string().trim().min(1).max(255),
});

export const platformPaperEditBody = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  examYear: zExamYear.optional(),
  items: z.array(teacherPaperItemBody).max(60).optional(),
});

export const platformPaperListQuery = z.object({
  board: zBoard.optional(),
  grade: zGrade.optional(),
  year: z.coerce.number().int().min(1990).max(2100).optional(),
  status: z.enum(['all', 'draft', 'published', 'withdrawn']).default('all'),
});

/**
 * One question of a PUBLISHED paper, edited in place. Its help and its tag are
 * always editable; its key (question, options, correct choice, answer) only
 * with `confirmKeyChange` once anyone has sat a paper carrying it — results
 * already recorded are not re-marked.
 */
export const publishedQuestionEditBody = z.object({
  item: teacherPaperItemBody,
  confirmKeyChange: z.boolean().optional().default(false),
});

/** Tag one question that is not a draft row — a published paper's, in the editor. */
export const platformTagBody = z.object({
  subjectId: z.string().min(1),
  question: z.string().trim().min(1).max(4_000),
  options: z.array(z.string().trim().max(500)).max(6).nullish(),
});

export type PlatformPaperDraftBody = z.infer<typeof platformPaperDraftBody>;
export type PlatformTagBody = z.infer<typeof platformTagBody>;
export type PlatformPaperCreateBody = z.infer<typeof platformPaperCreateBody>;
export type PlatformPaperUploadBody = z.infer<typeof platformPaperUploadBody>;
export type PlatformPaperEditBody = z.infer<typeof platformPaperEditBody>;
export type PlatformPaperListQuery = z.infer<typeof platformPaperListQuery>;
export type PublishedQuestionEditBody = z.infer<typeof publishedQuestionEditBody>;

/** Where a row of the list is in its life. A published paper is `published` or `withdrawn`. */
export type PlatformPaperState = CustomPaperStatus | 'withdrawn';

/** One row of `GET /platform/papers`. */
export interface PlatformPaperRow {
  /** DRAFT: a staged `CustomPaper`, `id` is its id. PAPER: a live GLOBAL `Paper`, `id` is its id. */
  kind: 'DRAFT' | 'PAPER';
  id: string;
  title: string;
  board: Board;
  grade: Grade;
  subjectId: string | null;
  subjectName: string | null;
  examYear: number | null;
  state: PlatformPaperState;
  /** How it was made: typed in, read from a PDF, or seeded with the curriculum. */
  source: 'MANUAL' | 'UPLOAD' | 'SEED';
  questionCount: number;
  totalMarks: number;
  error: string | null;
  /** Sittings of a published paper, across every school. A count, never who. */
  sittings: number;
  createdAt: string;
  updatedAt: string;
}

/** A staged paper, in the editor. */
export interface PlatformPaperDraftDTO {
  id: string;
  title: string;
  fileName: string | null;
  board: Board;
  grade: Grade;
  subjectId: string | null;
  subjectName: string | null;
  examYear: number | null;
  status: CustomPaperStatus;
  source: 'UPLOAD' | 'MANUAL';
  error: string | null;
  publishedPaperId: string | null;
  questionCount: number;
  totalMarks: number;
  items: CustomPaperItem[];
  createdAt: string;
  updatedAt: string;
}

/** A published row: the editor's item plus the Question it is. */
export type PublishedPaperItem = CustomPaperItem & { questionId: string };

/** A published GLOBAL paper, in the editor. */
export interface PublishedPaperDTO {
  id: string;
  title: string;
  board: Board;
  grade: Grade;
  subjectId: string;
  subjectName: string;
  examYear: number | null;
  source: string;
  withdrawnAt: string | null;
  sittings: number;
  totalMarks: number;
  totalSeconds: number;
  items: PublishedPaperItem[];
}

/** What re-tagging one row answers: the tag (or null), and who chose it. */
export interface PlatformTagResult {
  tag: { subtopicId: string; name: string; chapterName: string; sure: boolean } | null;
  /** `ai` when the model chose it, `rule` when the word-match stood in, null when neither found one. */
  by: 'ai' | 'rule' | null;
  /** Said on the page — e.g. that AI was unavailable and the word-match stood in. */
  note: string | null;
}

// ─────────────────────────── the help file ───────────────────────────

/** Written into every file, so a later format can be told apart from this one. */
export const PAPER_HELP_FORMAT = 'cwy.paperhelp/1';

/** The whole document, serialised. A dozen questions of help is a few kilobytes. */
export const PAPER_HELP_MAX_BYTES = 512 * 1024;

/** An empty string (or null) clears the field; an absent key leaves it alone. */
const clearable = (max: number) => z.string().trim().max(max).nullable().optional();

/** One worked step, as the bank stores it (`Question.workedExamples`). */
const workedStepSchema = z
  .object({
    step: z.string().trim().max(200).default(''),
    explanation: z.string().trim().min(1).max(1500),
  })
  .strict();

/**
 * One common mistake. A bare string is accepted and stored with weight 1, as
 * the seed stores one; the object form round-trips a weight the bank carries.
 */
const commonErrorSchema = z.union([
  z.string().trim().min(1).max(300),
  z
    .object({
      description: z.string().trim().min(1).max(300),
      weight: z.number().min(0).max(100).optional(),
    })
    .strict(),
]);

/**
 * One question's help. `number` and `prompt` are written into a download so
 * an author can see which question they are editing; they are ignored on
 * upload, because the question is named by its id and its text is not help.
 */
export const paperHelpQuestionSchema = z
  .object({
    number: z.number().int().min(1).optional(),
    prompt: z.string().max(10_000).optional(),
    /** The light hint, after a wrong first try. */
    tipsText: clearable(500),
    /** The strong hint, after a second. */
    strongHintText: clearable(800),
    /** One line naming the idea — the theory drawer's heading when no note exists. */
    theoryRef: clearable(1000),
    /** The third-attempt video. A YouTube watch/share link plays in place. */
    videoRef: clearable(500).refine((v) => !v || /^https:\/\//i.test(v), {
      error: 'must be an https:// link',
    }),
    workedExamples: z.array(workedStepSchema).max(20).optional(),
    commonErrors: z.array(commonErrorSchema).max(10).optional(),
  })
  .strict();

export const paperHelpSchema = z
  .object({
    format: z.literal(PAPER_HELP_FORMAT),
    /** Must be the paper it is uploaded to — a file for another paper is refused whole. */
    paperId: z.string().min(1).max(64),
    title: z.string().max(200).optional(),
    questions: z
      .record(z.string().min(1).max(64), paperHelpQuestionSchema)
      .refine((q) => Object.keys(q).length >= 1, { error: 'list at least one question' })
      .refine((q) => Object.keys(q).length <= 200, { error: 'at most 200 questions' }),
  })
  .strict();

export type PaperHelpQuestion = z.infer<typeof paperHelpQuestionSchema>;
export type PaperHelpContent = z.infer<typeof paperHelpSchema>;

/** PUT /platform/papers/published/:id/help — the file, read in the browser and sent as JSON. */
export const paperHelpUploadBody = z.object({
  fileName: z.string().trim().max(200).optional(),
  // Validated separately with `paperHelpSchema`, so the error names the field
  // in the author's file rather than `content.questions…`.
  content: z.unknown(),
});
export type PaperHelpUploadBody = z.infer<typeof paperHelpUploadBody>;

export interface PaperHelpUploadResult {
  /** Questions whose help changed. */
  updated: number;
  /** Questions in the file whose help was already exactly this. */
  unchanged: number;
}
